import { useState, useEffect } from 'react'
import { X, Printer, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Quotation modal — Finance generates a formal per-project cost quotation in the
 * exact layout of the company's "WHO DEVIS GENERIC" template:
 *   header logo · Date + Quotation # · To (client) · table [Description | Currency |
 *   Item Total | Line Total] · TOTAL · bank details footer.
 * Fetches GET /finance/quotation/{projectId} and prints a self-contained document.
 */

// Static company bank details (from the approved template).
const BANK_FOOTER = {
  company: '3 Pillars Data and Training Centre LLC',
  bank: 'Abu Dhabi Commercial Bank, P.O.Box, 2800 Abu Dhabi',
  iban: 'AE140030012927221920001',
  swift: 'ADCBAEAA',
  cid: '12927221',
}

export default function QuotationView({ projectId, onClose }) {
  const { t } = useTranslation('money')
  const { t: tc } = useTranslation('common')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.get(`/finance/quotation/${projectId}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load quotation'))
      .finally(() => setLoading(false))
  }, [projectId])

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const today = () => formatDate(new Date())

  const printQuotation = () => {
    if (!data) return
    const rows = data.line_items.map((li, i) => `
      <tr>
        <td style="padding:12px 14px;border:1px solid #d1d5db;font-size:13px;vertical-align:top;">
          <span style="font-weight:600;">${String.fromCharCode(97 + i)})</span> ${li.title}
        </td>
        <td style="padding:12px 14px;border:1px solid #d1d5db;font-size:13px;text-align:center;font-weight:700;">${data.currency}</td>
        <td style="padding:12px 14px;border:1px solid #d1d5db;font-size:13px;text-align:right;">${fmt(li.amount)}</td>
        <td style="padding:12px 14px;border:1px solid #d1d5db;font-size:13px;text-align:right;">${fmt(li.amount)}</td>
      </tr>`).join('')

    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Quotation ${data.quotation_number}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937; padding: 0; }
  @page { margin: 16mm 18mm; size: A4; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
  <!-- Logo -->
  <div style="margin-bottom:30px;">
    <table cellpadding="0" cellspacing="0"><tr>
      <td style="vertical-align:middle;padding-right:12px;">
        <div style="display:flex;gap:3px;align-items:flex-end;height:42px;">
          <span style="display:inline-block;width:7px;height:34px;background:#c79a3b;"></span>
          <span style="display:inline-block;width:7px;height:42px;background:#c79a3b;"></span>
          <span style="display:inline-block;width:7px;height:38px;background:#c79a3b;"></span>
        </div>
      </td>
      <td style="vertical-align:middle;">
        <div style="font-size:20px;font-weight:800;color:#b9892f;letter-spacing:1px;line-height:1.05;">3 PILLARS</div>
        <div style="font-size:12px;font-weight:600;color:#c79a3b;letter-spacing:1px;">DATA AND TRAINING CENTRE LLC</div>
      </td>
    </tr></table>
  </div>

  <!-- Date + To -->
  <table style="width:100%;margin-bottom:34px;"><tr>
    <td style="vertical-align:top;width:55%;">
      <div style="font-size:13px;"><strong>${t('date')}</strong>: ${today()}</div>
      <div style="font-size:13px;margin-top:4px;">QUOTATION #${data.quotation_number}</div>
    </td>
    <td style="vertical-align:top;width:45%;">
      <table cellpadding="0" cellspacing="0"><tr>
        <td style="vertical-align:top;font-size:13px;color:#6b7280;padding-right:10px;">To</td>
        <td style="vertical-align:top;font-size:13px;line-height:1.5;">
          ${data.client_name ? `<div>${data.client_name}</div>` : `<div>${data.project_name || ''}</div>`}
          ${data.client_org ? `<div>${data.client_org}</div>` : ''}
          ${data.external_ref ? `<div style="color:#6b7280;">Ref: ${data.external_ref}</div>` : ''}
        </td>
      </tr></table>
    </td>
  </tr></table>

  <!-- Line items table -->
  <table style="width:100%;border-collapse:collapse;">
    <thead><tr>
      <th style="padding:8px 14px;border:1px solid #d1d5db;text-align:left;font-size:13px;font-weight:600;background:#fff;">${t('description')}</th>
      <th style="padding:8px 14px;border:1px solid #d1d5db;text-align:center;font-size:13px;font-weight:600;width:90px;">${t('currency_plain')}</th>
      <th style="padding:8px 14px;border:1px solid #d1d5db;text-align:right;font-size:13px;font-weight:600;width:120px;">${t('item_total')}</th>
      <th style="padding:8px 14px;border:1px solid #d1d5db;text-align:right;font-size:13px;font-weight:600;width:120px;">${t('line_total')}</th>
    </tr></thead>
    <tbody>
      ${rows || `<tr><td colspan="4" style="padding:16px;border:1px solid #d1d5db;text-align:center;color:#9ca3af;font-size:13px;">${t('no_billable')}</td></tr>`}
      <tr>
        <td style="padding:12px 14px;border:1px solid #d1d5db;font-size:14px;font-weight:700;">${t('grand_total')}</td>
        <td style="padding:12px 14px;border:1px solid #d1d5db;text-align:center;font-size:14px;font-weight:700;">${data.currency}</td>
        <td style="padding:12px 14px;border:1px solid #d1d5db;"></td>
        <td style="padding:12px 14px;border:1px solid #d1d5db;text-align:right;font-size:14px;font-weight:800;">${fmt(data.total)}</td>
      </tr>
    </tbody>
  </table>

  <!-- Bank footer -->
  <div style="margin-top:40px;text-align:center;font-size:12px;color:#6b7280;line-height:1.7;">
    <div>Make payments to: ${BANK_FOOTER.company},</div>
    <div>BANK DETAILS: ${BANK_FOOTER.bank} IBAN: ${BANK_FOOTER.iban}</div>
    <div>${t('bic_swift')} <strong>${BANK_FOOTER.swift}</strong>.Clearing/CID: ${BANK_FOOTER.cid}</div>
    <div style="margin-top:14px;font-style:italic;font-weight:700;color:#1f2937;">${t('look_forward')}</div>
  </div>
</body></html>`

    const w = window.open('', '_blank', 'width=900,height=1100')
    w.document.write(html)
    w.document.close()
    setTimeout(() => { w.focus(); w.print() }, 400)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sticky top-0 z-10">
          <h2 className="font-semibold text-gray-800">{t('project_quotation')}</h2>
          <div className="flex items-center gap-2">
            {data && (
              <button onClick={printQuotation} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-100">
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6">
          {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>}
          {data && (
            <div>
              {/* Logo */}
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-end gap-0.5 h-9">
                  <span className="w-1.5 bg-amber-600" style={{ height: '28px' }} />
                  <span className="w-1.5 bg-amber-600" style={{ height: '36px' }} />
                  <span className="w-1.5 bg-amber-600" style={{ height: '32px' }} />
                </div>
                <div className="leading-tight">
                  <div className="text-lg font-extrabold text-amber-700 tracking-wide">3 PILLARS</div>
                  <div className="text-[10px] font-semibold text-amber-600 tracking-wide">DATA AND TRAINING CENTRE LLC</div>
                </div>
              </div>

              {/* Date + To */}
              <div className="flex justify-between mb-6 text-sm">
                <div>
                  <p><span className="font-semibold">{tc('labels.date')}</span>: {today()}</p>
                  <p className="mt-1">QUOTATION #{data.quotation_number}</p>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400">To</span>
                  <div className="leading-snug">
                    <p>{data.client_name || data.project_name}</p>
                    {data.client_org && <p>{data.client_org}</p>}
                    {data.external_ref && <p className="text-gray-500">Ref: {data.external_ref}</p>}
                  </div>
                </div>
              </div>

              {/* Table */}
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">{tc('labels.description')}</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold w-20">{tc('labels.currency')}</th>
                    <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold w-28">{t('item_total')}</th>
                    <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold w-28">{t('line_total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.line_items.length === 0 ? (
                    <tr><td colSpan={4} className="border border-gray-300 px-3 py-4 text-center text-gray-400 text-sm">{t('no_billable')}</td></tr>
                  ) : data.line_items.map((li, i) => (
                    <tr key={i}>
                      <td className="border border-gray-300 px-3 py-2.5 text-sm align-top">
                        <span className="font-semibold">{String.fromCharCode(97 + i)})</span> {li.title}
                      </td>
                      <td className="border border-gray-300 px-3 py-2.5 text-center text-sm font-bold">{data.currency}</td>
                      <td className="border border-gray-300 px-3 py-2.5 text-right text-sm">{fmt(li.amount)}</td>
                      <td className="border border-gray-300 px-3 py-2.5 text-right text-sm">{fmt(li.amount)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2.5 text-sm font-bold">{t('grand_total')}</td>
                    <td className="border border-gray-300 px-3 py-2.5 text-center text-sm font-bold">{data.currency}</td>
                    <td className="border border-gray-300 px-3 py-2.5"></td>
                    <td className="border border-gray-300 px-3 py-2.5 text-right text-sm font-extrabold">{fmt(data.total)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Bank footer */}
              <div className="text-center text-xs text-gray-500 leading-relaxed mt-8">
                <p>Make payments to: {BANK_FOOTER.company},</p>
                <p>BANK DETAILS: {BANK_FOOTER.bank} IBAN: <span data-private>{BANK_FOOTER.iban}</span></p>
                <p>{t('bic_swift')} <strong data-private>{BANK_FOOTER.swift}</strong>.Clearing/CID: <span data-private>{BANK_FOOTER.cid}</span></p>
                <p className="mt-3 italic font-bold text-gray-800">{t('look_forward')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
